import {computed, ref, onMounted, onUnmounted, watch} from "ui.vue3";
import {createSessionToken, fetchAddressSuggestions, loadPlacesLibrary} from "../lib/placesSearch";

const SEARCH_DEBOUNCE = 300;
const MIN_QUERY_LENGTH = 2;
const MAX_POINT_RESULTS = 5;

const normalize = (text) => String(text ?? '').toLowerCase().trim();

export default {
  name: 'MapSearchComponent',
  props: ['points', 'placeholder'],
  emits: ['select-point', 'select-address'],

  setup(props, {emit}) {
    const query = ref('');
    const opened = ref(false);
    const addressSuggestions = ref([]);
    const addressLoading = ref(false);
    const rootRef = ref(null);

    let sessionToken = null;
    let debounceTimeout = null;

    const matchedPoints = computed(() => {
      const value = normalize(query.value);
      if (value.length < MIN_QUERY_LENGTH) {
        return [];
      }
      return (props.points ?? [])
        .filter(point => {
          const raw = point?.raw ?? {};
          return normalize(raw.name).includes(value) || normalize(raw.address).includes(value);
        })
        .slice(0, MAX_POINT_RESULTS);
    });

    const hasResults = computed(() => matchedPoints.value.length > 0 || addressSuggestions.value.length > 0);

    const showDropdown = computed(() => opened.value && normalize(query.value).length >= MIN_QUERY_LENGTH);

    const requestAddresses = async () => {
      const value = query.value.trim();
      if (value.length < MIN_QUERY_LENGTH) {
        addressSuggestions.value = [];
        return;
      }
      addressLoading.value = true;
      const items = await fetchAddressSuggestions(value, sessionToken);
      if (value === query.value.trim()) {
        addressSuggestions.value = items;
      }
      addressLoading.value = false;
    };

    watch(query, () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
      debounceTimeout = setTimeout(() => {
        debounceTimeout = null;
        requestAddresses();
      }, SEARCH_DEBOUNCE);
    });

    const onFocus = () => {
      opened.value = true;
    };

    const onSelectPoint = (point) => {
      query.value = point?.raw?.name ?? '';
      opened.value = false;
      addressSuggestions.value = [];
      emit('select-point', point);
    };

    const onSelectAddress = (item) => {
      query.value = item.text;
      opened.value = false;
      addressSuggestions.value = [];
      emit('select-address', item);
      loadPlacesLibrary().then(lib => {
        sessionToken = createSessionToken(lib);
      });
    };

    const onClear = () => {
      query.value = '';
      addressSuggestions.value = [];
      opened.value = false;
    };

    const onDocumentClick = (event) => {
      if (rootRef.value && !rootRef.value.contains(event.target)) {
        opened.value = false;
      }
    };

    onMounted(async () => {
      document.addEventListener('click', onDocumentClick);
      const lib = await loadPlacesLibrary();
      sessionToken = createSessionToken(lib);
    });

    onUnmounted(() => {
      document.removeEventListener('click', onDocumentClick);
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
        debounceTimeout = null;
      }
    });

    return {
      query,
      opened,
      showDropdown,
      matchedPoints,
      addressSuggestions,
      addressLoading,
      hasResults,
      rootRef,
      onFocus,
      onSelectPoint,
      onSelectAddress,
      onClear,
    };
  },
  template: `
    <div ref="rootRef" class="partners-map-search" :class="{'partners-map-search--opened': showDropdown}">
      <div class="partners-map-search__field">
        <img class="partners-map-search__icon" src="/local/templates/new_site_v2/images/parthners_map/search.svg" alt="">
        <input
          class="partners-map-search__input"
          type="text"
          v-model="query"
          :placeholder="placeholder"
          @focus="onFocus"
        >
        <button class="partners-map-search__clear" type="button" @click="onClear">
          <img src="/local/templates/new_site_v2/images/parthners_map/close_modal.svg" alt="">
        </button>
      </div>
      <div v-if="showDropdown" class="partners-map-search__dropdown">
        <button
          v-for="point in matchedPoints"
          :key="point.id"
          class="partners-map-search__item"
          type="button"
          @click="onSelectPoint(point)"
        >
          <img class="partners-map-search__item-icon" src="/local/templates/new_site_v2/images/parthners_map/pin.svg" alt="">
          <span class="partners-map-search__item-text">
            <span class="partners-map-search__item-title">{{point.raw.name}}</span>
            <span v-if="point.raw.address" class="partners-map-search__item-subtitle">{{point.raw.address}}</span>
          </span>
        </button>
        <button
          v-for="item in addressSuggestions"
          :key="item.placeId"
          class="partners-map-search__item"
          type="button"
          @click="onSelectAddress(item)"
        >
          <img class="partners-map-search__item-icon" src="/local/templates/new_site_v2/images/parthners_map/pin.svg" alt="">
          <span class="partners-map-search__item-text">
            <span class="partners-map-search__item-title">{{item.text}}</span>
          </span>
        </button>
        <div v-if="!hasResults" class="partners-map-search__empty">
          {{ addressLoading ? 'Searching…' : 'Nothing found' }}
        </div>
      </div>
    </div>
  `,
}
